import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { checkCaptures, type CaptureValue, type PreviewLead, type ScriptField } from '@arcnet/contracts';
import { useLeadResult, useMyCampaigns, useNextLead } from '../../lib/api/hooks-dialler.js';
import { api } from '../../lib/api/client.js';
import { useSession } from '../../lib/auth/session.js';
import { relativeTime } from '../../lib/format.js';
import { Badge, ErrorBanner, Tab, Tabs } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import type { BrowserPhoneApi, CallEnd } from './useBrowserPhone.js';
import { ScriptText } from '../dialler/ScriptText.js';

/**
 * Preview and progressive campaigns in the workspace (docs/36 D1b, D2): take the next lead, see who
 * they are and why they are being called, call them from this phone, and say how it went. In a
 * progressive campaign the panel takes and dials each lead by itself while the agent is dialling --
 * preview with the Call click removed; the agent's own call is the agent leg, so it is agent-first. The outcome moves the
 * lead on by the campaign's own rules -- back later, done, a booked callback, or do-not-call.
 *
 * The lead stays on the screen until the agent says how the call went: a reloaded page gives the
 * same lead back (the API keeps it for the agent), so nobody is lost between two calls.
 */

const WHY: Record<PreviewLead['why'], { label: string; tone: 'info' | 'warn' | 'ok' | 'muted' }> = {
  callback: { label: 'Callback due', tone: 'warn' },
  yours: { label: 'Your lead', tone: 'ok' },
  retry: { label: 'Trying again', tone: 'muted' },
  fresh: { label: 'New', tone: 'info' },
};

/** What the network said, as an outcome. The agent can change it. */
function outcomeOf(end: CallEnd | null): string {
  if (!end) return 'no_answer';
  if (end.answered) return 'answered';
  const c = (end.cause ?? '').toLowerCase();
  if (c.includes('busy')) return 'busy';
  if (c.includes('no answer') || c.includes('timeout') || c.includes('unavailable') || c.includes('canceled')) return 'no_answer';
  return 'failed';
}

export function CampaignPanel({ tenantId, extensionId, phone, onActive, onDone }: {
  tenantId: string; extensionId: string; phone: BrowserPhoneApi;
  /** Whether a lead is on the screen: the workspace's own wrap-up steps aside for it. */
  onActive: (active: boolean) => void;
  onDone: () => void;
}) {
  const can = useSession((s) => s.can);
  const mine = useMyCampaigns(tenantId, extensionId, can('dialler:agent'));
  const next = useNextLead(tenantId, extensionId);
  const campaigns = mine.data?.data ?? [];
  const [campaignId, setCampaignId] = useState<string>('');
  const [lead, setLead] = useState<PreviewLead | null>(null);
  const [none, setNone] = useState<string | null>(null);
  // Where the agent is in the script, what they filled in and the disposition a button chose: kept
  // for the CONTACT, so moving on to their next number keeps it all.
  const [script, setScript] = useState<ScriptState>(EMPTY_SCRIPT);
  const recordId = lead?.campaign_record_id ?? '';
  useEffect(() => { setScript(EMPTY_SCRIPT); }, [recordId]);

  // Progressive (0139): the agent starts dialling and the panel takes and calls lead after lead by
  // itself, until they stop. Preview: an optional countdown dials the lead on the screen.
  const [running, setRunning] = useState(false);
  const [held, setHeld] = useState(false);
  const [left, setLeft] = useState<number | null>(null);
  const [retry, setRetry] = useState(0);
  const dialled = useRef(new Set<string>());
  useEffect(() => { setHeld(false); }, [lead?.attempt_id]);

  // The list comes in the agent's priority order (0136): start on the first with somebody to call.
  useEffect(() => {
    if (!campaignId && campaigns.length) {
      setCampaignId((campaigns.find((c) => c.due_now > 0 || c.callbacks_due > 0) ?? campaigns[0]!).id);
    }
  }, [campaigns, campaignId]);
  // A lead left open (a reload, another tab): take it straight back.
  const open = campaigns.find((c) => c.open_attempt_id && !lead);
  useEffect(() => {
    if (open && !next.isPending) {
      next.mutate(open.id, { onSuccess: (r) => { if ('attempt_id' in r) { setCampaignId(open.id); setLead(r); } } });
    }
  }, [open?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { onActive(lead !== null); }, [lead, onActive]);

  const current = campaigns.find((c) => c.id === campaignId);
  const progressive = current?.mode === 'progressive';
  // A campaign that stops being progressive (or goes away) stops the agent dialling it.
  useEffect(() => { if (!progressive) setRunning(false); }, [progressive]);
  const callEnded = lead && phone.lastEnd?.attemptId === lead.attempt_id && !phone.call ? phone.lastEnd : null;
  const onThisCall = lead !== null && phone.call !== null;
  const ready = phone.state === 'ready' && phone.call === null;

  // While dialling, the switch keeps inbound queues off this seat; the word lapses by itself if
  // the page goes away (API, campaigns.ts /dialling).
  useEffect(() => {
    if (!running || !campaignId) return;
    const say = (on: boolean) => api.post(
      `/v1/tenants/${tenantId}/extensions/${extensionId}/campaigns/${campaignId}/dialling`, { on }).catch(() => undefined);
    void say(true);
    const t = setInterval(() => { void say(true); }, 30_000);
    return () => { clearInterval(t); void say(false); };
  }, [running, campaignId, tenantId, extensionId]);

  // Progressive: free, phone on, nothing on the screen -- take the next lead. Nothing to call: look
  // again in twenty seconds.
  useEffect(() => {
    if (!running || lead || !ready || next.isPending || !campaignId) return;
    next.mutate(campaignId, {
      onSuccess: (r) => {
        if ('attempt_id' in r) { setNone(null); setLead(r); }
        else { setNone(r.none); setTimeout(() => setRetry((n) => n + 1), 20_000); }
      },
    });
  }, [running, lead, ready, retry, campaignId]); // eslint-disable-line react-hooks/exhaustive-deps

  // The countdown: progressive gives the agent two seconds to see who it is; preview what the
  // campaign set. Each call is dialled by itself once at most -- a call that failed waits for them.
  const autoSec = progressive ? (running ? PROGRESSIVE_GLANCE_SEC : null) : (current?.preview_autodial_sec ?? null);
  const armed = lead !== null && !callEnded && !onThisCall && ready && autoSec !== null && !held
    && !dialled.current.has(lead.attempt_id);
  const latest = useRef({ lead, phone });
  latest.current = { lead, phone };
  const dial = () => {
    const { lead: l, phone: p } = latest.current;
    if (!l) return;
    dialled.current.add(l.attempt_id);
    setLeft(null);
    p.dial(`+${l.phone}`, { attemptId: l.attempt_id, callerId: l.caller_id });
  };
  useEffect(() => {
    if (!armed || autoSec === null) { setLeft(null); return; }
    const from = Date.now();
    setLeft(autoSec);
    const t = setInterval(() => {
      const remain = autoSec - Math.floor((Date.now() - from) / 1000);
      if (remain <= 0) { clearInterval(t); dial(); } else setLeft(remain);
    }, 250);
    return () => clearInterval(t);
  }, [armed, autoSec, lead?.attempt_id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!can('dialler:agent') || campaigns.length === 0) return null;

  return (
    <div className="arc-card arc-card-body">
      <div className="arc-row" style={{ gap: 8, marginBottom: 10 }}>
        <Icon name="callOut" size={15} />
        <strong style={{ fontSize: 13 }}>Campaign</strong>
        <div className="arc-spacer" />
        {campaigns.length > 1 && !lead && !running ? (
          <select className="arc-select" style={{ maxWidth: 220 }} aria-label="Campaign" value={campaignId}
            onChange={(e) => { setCampaignId(e.target.value); setNone(null); }}>
            {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        ) : <span className="arc-muted" style={{ fontSize: 12.5 }}>{lead?.campaign.name ?? current?.name}</span>}
        {running && (
          <button type="button" className="arc-btn arc-btn-sm" onClick={() => setRunning(false)}
            title={lead ? 'Finish this lead, then stop' : undefined}>
            <Icon name="pause" size={12} /> Stop dialling
          </button>
        )}
      </div>

      {!lead && (
        <>
          <div className="arc-row" style={{ gap: 10 }}>
            {progressive ? (
              !running && (
                <button type="button" className="arc-btn arc-btn-primary" disabled={!campaignId || phone.state !== 'ready'}
                  onClick={() => { setNone(null); setRunning(true); }}>
                  <Icon name="play" size={12} /> Start dialling
                </button>
              )
            ) : (
              <button type="button" className="arc-btn arc-btn-primary" disabled={!campaignId || next.isPending || phone.call !== null}
                onClick={() => { setNone(null); next.mutate(campaignId, { onSuccess: (r) => { if ('attempt_id' in r) setLead(r); else setNone(r.none); } }); }}>
                Next lead
              </button>
            )}
            {running && !none && <span className="arc-muted" style={{ fontSize: 12.5 }}>{ready ? 'Getting the next lead…' : 'Waiting for the phone…'}</span>}
            {current && !running && (
              <span className="arc-muted" style={{ fontSize: 12.5 }}>
                {current.due_now} ready{current.callbacks_due ? `, ${current.callbacks_due} callback${current.callbacks_due === 1 ? '' : 's'} due` : ''}
              </span>
            )}
          </div>
          {progressive && phone.state !== 'ready' && <p className="arc-muted" style={{ fontSize: 12.5, marginTop: 8 }}>Switch the phone on to dial.</p>}
          {none && <p className="arc-muted" style={{ fontSize: 12.5, marginTop: 8 }}>{none}{running ? ' Looking again shortly.' : ''}</p>}
          {next.error && <ErrorBanner error={next.error} />}
        </>
      )}

      {lead && (
        <>
          <LeadCard lead={lead} />
          {lead.script && <ScriptPanel lead={lead} state={script} onChange={setScript} />}
          {!callEnded && (
            <div className="arc-row" style={{ gap: 8, marginTop: 12 }}>
              <button type="button" className="arc-btn arc-btn-primary" disabled={!ready} onClick={dial}>
                <Icon name="phone" size={13} /> {left !== null ? 'Call now' : `Call +${lead.phone}`}
              </button>
              {left !== null && (
                <>
                  <span className="arc-muted arc-num" style={{ fontSize: 12.5 }}>Calling in {left} s</span>
                  {!progressive && <button type="button" className="arc-btn" onClick={() => setHeld(true)}>Hold</button>}
                </>
              )}
              {!onThisCall && (
                <SkipButton tenantId={tenantId} extensionId={extensionId} attemptId={lead.attempt_id}
                  onDone={() => { setLead(null); onDone(); }} />
              )}
              {phone.state !== 'ready' && <span className="arc-muted" style={{ fontSize: 12.5 }}>Switch the phone on to call.</span>}
            </div>
          )}
          {callEnded && (
            <Outcome key={lead.attempt_id} tenantId={tenantId} extensionId={extensionId} lead={lead} end={callEnded} script={script}
              onNext={(next) => setLead(next)}
              onDone={() => { setLead(null); onDone(); void mine.refetch(); }} />
          )}
        </>
      )}
    </div>
  );
}

/** Progressive: how long the agent sees who is next before the call starts. */
const PROGRESSIVE_GLANCE_SEC = 2;

function LeadCard({ lead }: { lead: PreviewLead }) {
  const [history, setHistory] = useState(false);
  const who = [lead.first_name, lead.last_name].filter(Boolean).join(' ') || 'No name';
  const shown = lead.fields.filter((f) => f.value !== null && f.value !== '');
  return (
    <div>
      <div className="arc-row" style={{ gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 16 }}>{who}</strong>
        <Badge tone={WHY[lead.why].tone}>{WHY[lead.why].label}</Badge>
        <span className="arc-subtle" style={{ fontSize: 12 }}>try {lead.try_number} of {lead.tries}</span>
      </div>
      {/* Every number the contact has, in the order they are tried; this call is on the marked one. */}
      <div style={{ marginTop: 4 }}>
        {lead.numbers.map((n, i) => (
          <div key={n} style={{ fontSize: 13, fontWeight: i === lead.number_index ? 600 : 400, opacity: i < lead.number_index ? 0.55 : 1 }}>
            <span className="arc-mono">{i === lead.number_index ? '▸ ' : '  '}+{n}</span>
            {lead.numbers.length > 1 && <span className="arc-subtle" style={{ fontSize: 11.5, fontWeight: 400 }}> number {i + 1}</span>}
          </div>
        ))}
        {lead.email && <div className="arc-subtle" style={{ fontSize: 12.5 }}>{lead.email}</div>}
      </div>
      {lead.callback_note && <div className="arc-banner arc-banner-info" style={{ marginTop: 8 }}><Icon name="clock" size={13} /><span>{lead.callback_note}</span></div>}
      {shown.length > 0 && (
        <dl className="arc-kv" style={{ marginTop: 10 }}>
          {shown.map((f) => (
            // Text, never markup: these values came from the client's spreadsheet (docs/36 §8.3).
            <div key={f.key} style={{ display: 'contents' }}><dt>{f.label}</dt><dd>{typeof f.value === 'boolean' ? (f.value ? 'Yes' : 'No') : String(f.value)}</dd></div>
          ))}
        </dl>
      )}
      {lead.history.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" onClick={() => setHistory((v) => !v)}>
            {history ? 'Hide' : 'Show'} earlier calls ({lead.history.length})
          </button>
          {history && (
            <ul style={{ fontSize: 12.5, margin: '6px 0 0', paddingLeft: 18 }}>
              {lead.history.map((h, i) => (
                <li key={i}>{relativeTime(h.at)} to +{h.phone}{h.agent ? `, ${h.agent}` : ''}: {h.disposition ?? h.outcome ?? '—'}{h.note ? ` — ${h.note}` : ''}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function SkipButton({ tenantId, extensionId, attemptId, onDone }: { tenantId: string; extensionId: string; attemptId: string; onDone: () => void }) {
  const result = useLeadResult(tenantId, extensionId);
  return (
    <button type="button" className="arc-btn" disabled={result.isPending}
      onClick={() => result.mutate({ attemptId, outcome: 'cancelled' }, { onSuccess: onDone })}>Skip</button>
  );
}

const OUTCOMES: ReadonlyArray<readonly [string, string]> = [
  ['answered', 'Spoke to them'], ['no_answer', 'No answer'], ['busy', 'Busy'], ['machine', 'Voicemail'], ['failed', 'Could not connect'],
];

type Then = 'auto' | 'redial' | 'callback';

/**
 * How the call went, and -- when nobody was reached -- what happens next (owner, 26 September
 * 2026): the contact's next number straight away (or its next try later), a redial now, or a
 * callback at a time the agent books.
 */
function Outcome({ tenantId, extensionId, lead, end, script, onNext, onDone }: {
  tenantId: string; extensionId: string; lead: PreviewLead; end: CallEnd; script: ScriptState;
  onNext: (next: PreviewLead) => void; onDone: () => void;
}) {
  const result = useLeadResult(tenantId, extensionId);
  // The campaign's own dispositions and rules, sent with the lead (0136).
  const rules = lead.wrap_up;
  const [reference, setReference] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [outcome, setOutcome] = useState(outcomeOf(end));
  const [disposition, setDisposition] = useState(script.disposition ?? '');
  const [note, setNote] = useState('');
  const [then, setThen] = useState<Then>('auto');
  const [callbackAt, setCallbackAt] = useState('');
  const [mine, setMine] = useState(true);
  const [dnc, setDnc] = useState(false);
  const active = rules.dispositions;
  const chosen = active.find((c) => c.code === disposition);
  const spoke = outcome === 'answered';
  const needsNote = (chosen?.requires_note ?? false) || (spoke && rules.note_required);
  const needsReference = spoke && rules.reference_required && !reference.trim();
  const needsTags = spoke && rules.tags_required && tags.length === 0;
  const needsDisposition = outcome === 'answered' && !disposition;
  // The script's fields: checked here as the API will check them, so the agent is told before saving.
  const captures = cleanCaptures(script.captures);
  const scriptCheck = lead.script ? checkCaptures(lead.script.body, captures, disposition || null) : null;
  const scriptWhy = scriptCheck && !scriptCheck.ok ? scriptCheck.why : null;
  // Reached: answered with a final disposition. Anything else is "not reached", and gets the choice.
  const reached = outcome === 'answered' && (!chosen || chosen.effect === 'final');
  const nextNumber = lead.numbers[lead.number_index + 1];
  const autoLabel = chosen?.effect === 'next_number' || !reached
    ? nextNumber ? `Call their next number now (+${nextNumber})`
      : chosen?.effect === 'next_number' ? 'No other number: stop calling this contact'
        : lead.try_number < lead.tries ? `Try again later (try ${lead.try_number + 1} of ${lead.tries})`
          : `That was the last try: stop calling this contact`
    : '';

  return (
    <form style={{ marginTop: 12, borderTop: '1px solid var(--arc-border)', paddingTop: 12 }} onSubmit={(e: FormEvent) => {
      e.preventDefault();
      result.mutate({
        attemptId: lead.attempt_id, outcome, disposition: disposition || null, note: note.trim() || null,
        reference: reference.trim() || null, tags,
        then: !reached && then !== 'auto' ? then : null,
        callback_at: !reached && then === 'callback' && callbackAt ? new Date(callbackAt).toISOString() : null,
        callback_mine: mine, do_not_call: dnc,
        ...(lead.script ? { captures } : {}),
      }, {
        onSuccess: (r) => {
          const res = r as { lead: string; next?: PreviewLead };
          if (res.lead === 'continue' && res.next) onNext(res.next); else onDone();
        },
      });
    }}>
      <strong style={{ fontSize: 13 }}>How did it go?</strong>
      <div className="arc-row" style={{ gap: 6, flexWrap: 'wrap', margin: '8px 0' }}>
        {OUTCOMES.map(([k, label]) => (
          <button key={k} type="button" className={`arc-btn arc-btn-sm ${outcome === k ? 'arc-btn-primary' : ''}`} onClick={() => setOutcome(k)}>{label}</button>
        ))}
      </div>
      <div className="arc-field" style={{ margin: 0, maxWidth: 320 }}>
        <label className="arc-label" htmlFor="cr-disp">Disposition{outcome === 'answered' ? '' : ' (optional)'}</label>
        <select id="cr-disp" className="arc-select" value={disposition} onChange={(e) => setDisposition(e.target.value)}>
          <option value="">Choose…</option>
          {active.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
        </select>
      </div>

      {!reached && (
        <fieldset style={{ border: 'none', padding: 0, margin: '10px 0 0' }}>
          <legend className="arc-label" style={{ marginBottom: 4 }}>Then</legend>
          <label className="arc-row" style={{ gap: 6, fontSize: 12.5 }}>
            <input type="radio" name="then" checked={then === 'auto'} onChange={() => setThen('auto')} /> {autoLabel}
          </label>
          <label className="arc-row" style={{ gap: 6, fontSize: 12.5 }}>
            <input type="radio" name="then" checked={then === 'redial'} onChange={() => setThen('redial')} /> Redial +{lead.phone} now
          </label>
          <label className="arc-row" style={{ gap: 6, fontSize: 12.5 }}>
            <input type="radio" name="then" checked={then === 'callback'} onChange={() => setThen('callback')} /> Call back at a set time
          </label>
          {then === 'callback' && (
            <div className="arc-row" style={{ gap: 10, marginTop: 6, flexWrap: 'wrap' }}>
              <input className="arc-input" type="datetime-local" value={callbackAt} aria-label="Call back at"
                onChange={(e) => setCallbackAt(e.target.value)} style={{ maxWidth: 230 }} />
              <label className="arc-row" style={{ gap: 6, fontSize: 12.5 }}>
                <input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} /> Only me
              </label>
            </div>
          )}
        </fieldset>
      )}

      {rules.reference_label && (
        <div className="arc-field" style={{ margin: '10px 0 0', maxWidth: 320 }}>
          <label className="arc-label" htmlFor="cr-ref">{rules.reference_label}{rules.reference_required && spoke ? '' : ' (optional)'}</label>
          <input id="cr-ref" className="arc-input" maxLength={100} value={reference} onChange={(e) => setReference(e.target.value)} />
        </div>
      )}
      {rules.tags.length > 0 && (
        <div className="arc-field" style={{ margin: '10px 0 0' }}>
          <span className="arc-label">Tags{rules.tags_required && spoke ? '' : ' (optional)'}</span>
          <div className="arc-row" style={{ gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
            {rules.tags.map((t) => {
              const on = tags.includes(t);
              return (
                <button key={t} type="button" className={`arc-btn arc-btn-sm ${on ? 'arc-btn-primary' : ''}`} aria-pressed={on}
                  onClick={() => setTags(on ? tags.filter((x) => x !== t) : [...tags, t].slice(0, 10))}>{t}</button>
              );
            })}
          </div>
        </div>
      )}
      <div className="arc-field" style={{ margin: '10px 0 0' }}>
        <label className="arc-label" htmlFor="cr-note">Note{needsNote ? '' : ' (optional)'}</label>
        <textarea id="cr-note" className="arc-input" rows={2} maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
      <label className="arc-row" style={{ gap: 6, fontSize: 12.5, marginTop: 8 }}>
        <input type="checkbox" checked={dnc} onChange={(e) => setDnc(e.target.checked)} /> They asked not to be called again
      </label>
      <div className="arc-row" style={{ gap: 10, marginTop: 12 }}>
        <button type="submit" className="arc-btn arc-btn-primary"
          disabled={result.isPending || needsDisposition || !!scriptWhy || (needsNote && !note.trim()) || needsReference || needsTags || (!reached && then === 'callback' && !callbackAt)}>
          Save
        </button>
        {needsDisposition && <span className="arc-muted" style={{ fontSize: 12.5 }}>Choose what the call was.</span>}
        {!needsDisposition && scriptWhy && <span className="arc-muted" style={{ fontSize: 12.5 }}>{scriptWhy}</span>}
        {!needsDisposition && !scriptWhy && needsReference && <span className="arc-muted" style={{ fontSize: 12.5 }}>Enter the {rules.reference_label}.</span>}
        {!needsDisposition && !scriptWhy && !needsReference && needsTags && <span className="arc-muted" style={{ fontSize: 12.5 }}>Choose at least one tag.</span>}
        {!needsDisposition && !scriptWhy && !needsReference && !needsTags && needsNote && !note.trim() && <span className="arc-muted" style={{ fontSize: 12.5 }}>Add a note.</span>}
      </div>
      {result.error && <ErrorBanner error={result.error} />}
    </form>
  );
}

// ================================================================================== the script

interface ScriptState { step: number; captures: Record<string, CaptureValue>; disposition: string | null }
const EMPTY_SCRIPT: ScriptState = { step: 0, captures: {}, disposition: null };

/** Only what was filled in: an empty field is left alone on the lead, not cleared. */
function cleanCaptures(c: Record<string, CaptureValue>): Record<string, CaptureValue> {
  return Object.fromEntries(Object.entries(c).filter(([, v]) => v !== null && v !== ''));
}

/**
 * The campaign's script beside the lead (docs/36 §8.3): the step's words with the lead's details
 * in them, buttons to move on (a button can choose the disposition), and the fields to fill in.
 * What the agent fills in is a value in the script at once.
 */
function ScriptPanel({ lead, state, onChange }: { lead: PreviewLead; state: ScriptState; onChange: (s: ScriptState) => void }) {
  const steps = lead.script!.body.steps;
  const step = steps[Math.min(state.step, steps.length - 1)]!;
  const vars: Record<string, string | null> = { ...lead.vars };
  for (const [k, v] of Object.entries(state.captures)) {
    if (v !== null && v !== '') vars[`custom.${k}`] = typeof v === 'boolean' ? (v ? 'Yes' : 'No') : String(v);
  }
  const go = (key: string | null, disposition: string | null) => {
    const i = key ? steps.findIndex((s) => s.key === key) : -1;
    onChange({ ...state, step: i >= 0 ? i : state.step, disposition: disposition ?? state.disposition });
  };

  return (
    <div style={{ marginTop: 12, borderTop: '1px solid var(--arc-border)', paddingTop: 12 }}>
      {steps.length > 1 && (
        <Tabs style={{ marginBottom: 8 }}>
          {steps.map((s, i) => (
            <Tab key={s.key} active={i === state.step} onSelect={() => onChange({ ...state, step: i })}>{s.title}</Tab>
          ))}
        </Tabs>
      )}
      <ScriptText text={step.text} vars={vars} />
      {step.fields.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginTop: 6 }}>
          {step.fields.map((f) => (
            <CaptureInput key={f.key} field={f} value={state.captures[f.key] ?? null}
              onChange={(v) => onChange({ ...state, captures: { ...state.captures, [f.key]: v } })} />
          ))}
        </div>
      )}
      {step.buttons.length > 0 && (
        <div className="arc-row" style={{ gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
          {step.buttons.map((b, i) => (
            <button key={i} type="button" className={`arc-btn arc-btn-sm ${b.disposition && b.disposition === state.disposition ? 'arc-btn-primary' : ''}`}
              onClick={() => go(b.go_to, b.disposition)}>{b.label}</button>
          ))}
        </div>
      )}
    </div>
  );
}

function CaptureInput({ field, value, onChange }: { field: ScriptField; value: CaptureValue; onChange: (v: CaptureValue) => void }) {
  const id = `cap-${field.key}`;
  const label = <label className="arc-label" htmlFor={id}>{field.label}{field.required_for.length ? ' *' : ''}</label>;
  switch (field.type) {
    case 'yes_no':
      return (
        <div className="arc-field" style={{ margin: 0 }}>
          {label}
          <select id={id} className="arc-select" value={value === null ? '' : value ? 'yes' : 'no'}
            onChange={(e) => onChange(e.target.value === '' ? null : e.target.value === 'yes')}>
            <option value="">—</option><option value="yes">Yes</option><option value="no">No</option>
          </select>
        </div>
      );
    case 'choice':
      return (
        <div className="arc-field" style={{ margin: 0 }}>
          {label}
          <select id={id} className="arc-select" value={typeof value === 'string' ? value : ''} onChange={(e) => onChange(e.target.value || null)}>
            <option value="">Choose…</option>
            {field.options.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
      );
    default:
      return (
        <div className="arc-field" style={{ margin: 0 }}>
          {label}
          <input id={id} className="arc-input" type={field.type === 'date' ? 'date' : 'text'} inputMode={field.type === 'number' ? 'decimal' : undefined}
            maxLength={500} value={value === null ? '' : String(value)} onChange={(e) => onChange(e.target.value)} />
        </div>
      );
  }
}
